import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Descriptions,
  Drawer,
  Empty,
  Image,
  List,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  theme,
  Flex
} from 'antd';
import { useParams } from 'react-router-dom';
import {
  BugOutlined,
  RobotOutlined,
  FileOutlined,
  AudioOutlined,
  NodeIndexOutlined
} from '@ant-design/icons';
import { getSessionHistory } from '../../services/conversation-logs';
import { formatDateTime } from '../../utils';
import { toast } from 'sonner';
import ChatBotDataContext from "../../context/chatbot-data";

const { Text, Title } = Typography;

export default function LogsView() {
  const { sessionId } = useParams();
  const [sessionData, setSessionData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [chatbotData] = useContext(ChatBotDataContext) || [{}];
  const nodes = chatbotData?.nodes || {};

  const { token } = theme.useToken();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    setLoading(true);
    getSessionHistory(sessionId, page, perPage)
      .then(({ data, total }) => {
        setSessionData((old) => [...old, ...data]);
        setTotal(total);
      })
      .catch((e) => {
        toast.error('Falha ao carregar histórico');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, perPage, sessionId]);

  const openDebug = (log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const closeDebug = () => {
    setDrawerOpen(false);
    setSelectedLog(null);
  };

  const loadMoreButton = useMemo(
    () =>
      !loading && total > sessionData.length ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Button shape="round" onClick={() => setPage((old) => ++old)}>Carregar mais mensagens</Button>
        </div>
      ) : null,
    [loading, total, sessionData.length]
  );

  return (
    <Flex vertical style={{ height: '100%', position: 'relative', background: token.colorBgLayout }}> 
      {/* Header Info */}
      <Flex 
        justify="space-between" 
        align="center" 
        style={{ 
            padding: '12px 24px', 
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgContainer,
            zIndex: 10
        }}
      >
        <div>
           <div style={{ fontSize: 12, opacity: 0.6 }}>Sessão</div>
           <Text strong copyable>{sessionId}</Text>
        </div>
        <div style={{ textAlign: 'right' }}>
             <div style={{ fontSize: 12, opacity: 0.6 }}>Iniciada em</div>
             <Text strong>{sessionData.length > 0 ? formatDateTime(sessionData[0].createdAt) : '-'}</Text>
        </div>
      </Flex>

      {/* Main Split Content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <Flex vertical style={{ width: '100%' }}>
            {sessionData.map((item) => (
              <LogEntry key={item.id} item={item} onOpenDebug={() => openDebug(item)} token={token} nodes={nodes} />
            ))}
            {loading && <div style={{ padding: 24 }}><Skeleton active avatar paragraph={{ rows: 2 }} /></div>}
            {loadMoreButton}
        </Flex>
      </div>

      <DebugDrawer open={drawerOpen} onClose={closeDebug} data={selectedLog} token={token} />
    </Flex>
  );
}

const LogEntry = ({ item, onOpenDebug, token, nodes }) => {
  const { message, state, createdAt } = item;
  
  const timeString = new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const getNodeName = (id) => {
      const node = nodes[id];
      if (!node) return id;
      return node.data?.label || node.label || node.title || node.name || id;
  };

  const renderBotResponse = (responseItem, index) => {
    const type = responseItem.type;
    const data = responseItem.data;

    if (type === 'text') {
      return <Text key={index} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{data}</Text>;
    }
    if (type === 'options') {
       const options = Array.isArray(data) ? data : Object.values(data);
       return (
         <Flex wrap="wrap" gap={8} key={index} style={{ marginTop: 8 }}>
           {options.map((opt, i) => (
             <div 
                key={i} 
                style={{ 
                    padding: '4px 12px', 
                    borderRadius: 999, 
                    fontSize: 12, 
                    border: `1px solid ${token.colorPrimary}`, 
                    color: token.colorPrimary,
                    cursor: 'default',
                    opacity: 0.8
                }}
             >
                {opt.text || opt.label}
             </div>
           ))}
         </Flex>
       );
    }
    if (type === 'image' || type === 'imageV2') {
        const src = type === 'imageV2' ? data.data : data;
        const caption = type === 'imageV2' ? data.caption : null;
        return (
            <div key={index} style={{ marginTop: 8 }}>
                <Image src={src} width={200} style={{ borderRadius: 8, objectFit: 'cover' }} />
                {caption && <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{caption}</div>}
            </div>
        )
    }
    if (type === 'audio') {
        return (
            <Flex key={index} align="center" gap={8} style={{ marginTop: 8, padding: 8, borderRadius: 8, background: token.colorFillAlter }}>
                 <AudioOutlined /> <span style={{ fontSize: 12 }}>Áudio</span>
                 <audio controls src={data} style={{ height: 32, width: 160 }} />
            </Flex>
        )
    }
    if (type === 'file') {
         return (
             <div key={index} style={{ marginTop: 8 }}>
                 <Button icon={<FileOutlined />} size="small" href={data} target="_blank">Abrir Arquivo</Button>
             </div>
         )
    }
    
    return <Text key={index} type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>Unsupported type: {type}</Text>;
  };

  return (
    <Flex 
        style={{ 
            width: '100%', 
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgContainer 
        }} 
        align="stretch"
    >
      {/* Left Column: Chat Interaction (60-70%) */}
      <div style={{ flex: 1, padding: 32, maxWidth: '65%' }}>
          <Flex vertical gap={24}>
              {/* User Message */}
              <Flex justify="end">
                  <Flex vertical align="end" style={{ maxWidth: '90%' }}>
                      <div 
                        style={{ 
                            padding: '12px 20px', 
                            borderRadius: '16px 16px 0 16px', 
                            background: token.colorPrimary, 
                            color: '#fff',
                            boxShadow: token.boxShadowTertiary
                        }}
                      >
                          <div style={{ fontSize: '1rem' }}>{message}</div>
                          <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right' }}>{timeString}</div>
                      </div>
                  </Flex>
              </Flex>

              {/* Bot Response */}
              {state?.response?.length > 0 ? (
                <Flex justify="start">
                    <Flex align="start" gap={12} style={{ maxWidth: '90%' }}>
                        <Avatar 
                            size="small" 
                            icon={<RobotOutlined />} 
                            style={{ marginTop: 8, background: token.colorFillContent, color: token.colorTextSecondary }} 
                        />
                        <div 
                            style={{ 
                                padding: '12px 20px', 
                                borderRadius: '16px 16px 16px 0', 
                                background: token.colorBgLayout, // Subtle background contrast
                                color: token.colorText
                            }}
                        >
                            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                {state.response.map((res, i) => renderBotResponse(res, i))}
                            </Space>
                            <div style={{ fontSize: 10, marginTop: 4, opacity: 0.5, textAlign: 'right' }}>{timeString}</div>
                        </div>
                    </Flex>
                </Flex>
              ) : (
                 <Flex justify="start">
                      <Flex align="center" gap={8} style={{ opacity: 0.6 }}>
                           <Avatar size="small" icon={<RobotOutlined />} />
                           <Text type="secondary" italic>Sem resposta visível</Text>
                      </Flex>
                 </Flex>
              )}
          </Flex>
      </div>

      {/* Right Column: Technical Details (30-40%) */}
      <div 
        style={{ 
            width: 380, 
            minWidth: 380, 
            padding: 32, 
            background: token.colorFillQuaternary, // Very subtle background diff
            borderLeft: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            flexDirection: 'column'
        }}
      >
          <Flex justify="space-between" align="center" style={{ marginBottom: 24 }}>
             <Space size={8}>
                <NodeIndexOutlined style={{ color: token.colorPrimary }} />
                <Text strong style={{ fontSize: 12, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Fluxo de Execução</Text>
             </Space>
             <Button size="small" icon={<BugOutlined />} onClick={onOpenDebug}>Debug JSON</Button>
          </Flex>

          <div style={{ flex: 1 }}>
              {state?.processed_nodes && state.processed_nodes.length > 0 ? (
                 <Flex vertical style={{ position: 'relative' }}>
                {state.processed_nodes.map((nodeId, i) => {
                    const isLast = i === state.processed_nodes.length - 1;
                    const isFinal = nodeId === state.final_node;
                    const nodeName = getNodeName(nodeId);
                    
                    return (
                        <div key={i} style={{ display: 'flex', position: 'relative', paddingBottom: isLast ? 0 : 16 }}>
                            {/* Connecting Line */}
                            {!isLast && (
                                <div 
                                    style={{ 
                                        position: 'absolute', 
                                        left: 5, 
                                        top: 12, 
                                        bottom: -4, 
                                        width: 2, 
                                        background: token.colorBorderSecondary,
                                        borderRadius: 1 
                                    }} 
                                />
                            )}
                            
                            {/* Dot Indicator */}
                            <div 
                                style={{ 
                                    width: 12, 
                                    height: 12, 
                                    borderRadius: '50%', 
                                    background: (isLast || isFinal) ? token.colorSuccess : token.colorFillContent, 
                                    border: `2px solid ${token.colorBgContainer}`,
                                    boxShadow: (isLast || isFinal) ? token.boxShadowSecondary : 'none',
                                    zIndex: 1,
                                    marginRight: 12,
                                    flexShrink: 0,
                                    marginTop: 2
                                }} 
                            />
                            
                            {/* Label */}
                            <div style={{ lineHeight: 1.2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                                <Text 
                                    style={{ 
                                        fontSize: 11, 
                                        color: (isLast || isFinal) ? token.colorText : token.colorTextSecondary,
                                        fontWeight: (isLast || isFinal) ? 600 : 400
                                    }}
                                >
                                    {nodeName}
                                </Text>
                                {isFinal && (
                                    <Tag 
                                        bordered={false} 
                                        color="success" 
                                        style={{ fontSize: 9, lineHeight: '14px', padding: '0 4px', margin: 0 }}
                                    >
                                        FINAL
                                    </Tag>
                                )}
                            </div>
                        </div>
                    );
                })}
                 </Flex>
              ) : (
                <Empty description="Sem dados de fluxo" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
          </div>
          
          {/* Quick Stats Footer in Tech Column */}
          <Flex gap={8} wrap="wrap" style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
              {state?.intents?.[0] && (
                  <Tag bordered={false} color="processing">#{state.intents[0].name} ({(state.intents[0].confidence * 100).toFixed(0)}%)</Tag>
              )}
              {state?.entities?.length > 0 && (
                  <Tag bordered={false} color="warning">{state.entities.length} Entidades</Tag>
              )}
          </Flex>
      </div>
    </Flex>
  );
};

const DebugDrawer = ({ open, onClose, data, token }) => {
  if (!data) return null;
  const { state, ip, responseTime, id, updatedAt } = data;

  const items = [
    {
      key: '1',
      label: 'Geral',
      children: (
         <div style={{ padding: 16 }}>
           <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Log ID"><Text copyable>{id}</Text></Descriptions.Item>
              <Descriptions.Item label="IP">{ip}</Descriptions.Item>
              <Descriptions.Item label="Tempo de Resposta">{responseTime ? `${(responseTime / 1000).toFixed(4)}s` : 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Versão Snapshot">{state?.snapshot_version}</Descriptions.Item>
              <Descriptions.Item label="Atualizado Em">{formatDateTime(updatedAt)}</Descriptions.Item>
           </Descriptions>
         </div>
      ),
    },
    {
      key: '2',
      label: 'Intenções',
      children: (
        <div style={{ padding: 16 }}>
          <List
             dataSource={state?.intents || []}
             renderItem={item => (
               <List.Item>
                  <Flex justify="space-between" style={{ width: '100%' }}>
                     <Text strong>#{item.name}</Text>
                     <Tag color={item.confidence > 0.7 ? 'green' : 'orange'}>
                        {(item.confidence * 100).toFixed(1)}%
                     </Tag>
                  </Flex>
               </List.Item>
             )}
             locale={{ emptyText: 'Nenhuma intenção detectada' }}
          />
        </div>
      ),
    },
    {
      key: '3',
      label: 'Entidades',
      children: (
        <div style={{ padding: 16 }}>
          <List
             dataSource={state?.entities || []}
             renderItem={item => (
               <List.Item>
                  <div style={{ width: '100%' }}>
                     <Flex justify="space-between" style={{ marginBottom: 4 }}>
                         <Text strong>@{item.entity}</Text>
                         <Tag color="cyan">{(item.confidence * 100).toFixed(1)}%</Tag>
                     </Flex>
                     <div style={{ padding: 4, borderRadius: 4, fontSize: 12, background: token.colorFillAlter }}>
                        Valor: <Text code>{item.value}</Text>
                     </div>
                  </div>
               </List.Item>
             )}
             locale={{ emptyText: 'Nenhuma entidade detectada' }}
          />
        </div>
      ),
    },
    {
        key: '4',
        label: 'Contexto',
        children: (
            <div style={{ maxHeight: '60vh', overflow: 'auto', padding: 16 }}>
                {state?.context ? (
                     <pre style={{ fontSize: 12, padding: 8, borderRadius: 8, background: '#1f1f1f', color: '#52c41a' }}>
                         {JSON.stringify(state.context, null, 2)}
                     </pre>
                ) : <Empty description="Contexto vazio" />}
            </div>
        )
    },
    {
        key: '5',
        label: 'Nodes',
        children: (
            <div style={{ padding: 16 }}>
                 <Timeline mode="left">
                     {state?.processed_nodes?.map((nodeId, i) => (
                         <Timeline.Item key={i} color={i === state.processed_nodes.length - 1 ? 'green' : 'blue'}>
                             <Text type="secondary" style={{ fontSize: 12 }}>Passo {i + 1}</Text>
                             <br />
                             <Text copyable code>{nodeId}</Text>
                             {nodeId === state.final_node && <Tag style={{ marginLeft: 8 }} color="green">Final</Tag>}
                         </Timeline.Item>
                     ))}
                 </Timeline>
                 {(!state?.processed_nodes || state.processed_nodes.length === 0) && <Empty description="Nenhum nó processado" />}
            </div>
        )
    },
    {
        key: '6',
        label: 'Raw JSON',
        children: (
            <div style={{ maxHeight: '60vh', overflow: 'auto', padding: 16 }}>
                 <pre style={{ fontSize: 12, padding: 8, borderRadius: 8, background: '#141414', color: '#d9d9d9' }}>
                     {JSON.stringify(data, null, 2)}
                 </pre>
            </div>
        )
    }
  ];

  return (
    <Drawer
      title="Detalhes do Processamento"
      placement="right"
      width={600}
      onClose={onClose}
      open={open}
      styles={{ body: { padding: 0 } }}
    >
      <Tabs defaultActiveKey="1" items={items} tabBarStyle={{ padding: '0 16px' }} />
    </Drawer>
  );
};